// Per-user Accounting access grants (master-admin settings → Accounting).
// Mirrors the backend User::canAccounting(): master admins get everything by
// default (until narrowed explicitly), everyone else — regular admins included —
// is purely grant-based via the user's `accounting_access` array.

export const ACCOUNTING = {
  TAB: "accounting_tab",
  PNL: "pnl",
  RECONCILIATION: "reconciliation",
  UPLOAD_STATEMENTS: "upload_statements",
  VOID_INVOICE: "void_invoice",
  // Per-section inline-edit grants for /pnl. Each unlocks one column group for
  // direct editing; PNL (view) is still required to reach the page, so an edit
  // grant alone does nothing. Keys mirror User::ACCOUNTING_PNL_EDIT_*.
  PNL_EDIT_STOCK: "pnl_edit_stock",
  PNL_EDIT_SUPPLIER: "pnl_edit_supplier",
  PNL_EDIT_WATCH: "pnl_edit_watch",
  PNL_EDIT_BUY_PRICE: "pnl_edit_buy_price",
  PNL_EDIT_EXPENSES: "pnl_edit_expenses",
  PNL_EDIT_VAT: "pnl_edit_vat",
};

// The options shown in the master-admin Accounting multi-select, in order.
// View grants first, then the P&L edit grants — the list is long enough now
// that mixing them would make it hard to see what someone can actually change.
export const ACCOUNTING_OPTIONS = [
  { key: ACCOUNTING.TAB, label: "Accounting tab view" },
  { key: ACCOUNTING.PNL, label: "P&L view" },
  { key: ACCOUNTING.RECONCILIATION, label: "Reconciliation view" },
  { key: ACCOUNTING.UPLOAD_STATEMENTS, label: "Upload statements to reconcile" },
  { key: ACCOUNTING.VOID_INVOICE, label: "Void Invoice" },
  { key: ACCOUNTING.PNL_EDIT_STOCK, label: "P&L edit — Stock Purchase" },
  { key: ACCOUNTING.PNL_EDIT_SUPPLIER, label: "P&L edit — Supplier" },
  { key: ACCOUNTING.PNL_EDIT_WATCH, label: "P&L edit — Watch details" },
  { key: ACCOUNTING.PNL_EDIT_BUY_PRICE, label: "P&L edit — Buy Price" },
  { key: ACCOUNTING.PNL_EDIT_EXPENSES, label: "P&L edit — Expenses" },
  { key: ACCOUNTING.PNL_EDIT_VAT, label: "P&L edit — VAT" },
];

export const accountingLabel = (key) =>
  ACCOUNTING_OPTIONS.find((o) => o.key === key)?.label || key;

/**
 * True if the user may use an Accounting capability.
 *
 * Authoritative source is `accounting_capabilities` — the backend-resolved list
 * (master admins with no explicit grant get all keys). Using it guarantees the
 * frontend gate and the server gate can never disagree. The `accounting_access`
 * branch is only a fallback for stale user objects (e.g. a localStorage user
 * cached before this field existed) so the gate still fails closed.
 */
export const canAccounting = (user, key) => {
  if (!user) return false;
  if (Array.isArray(user.accounting_capabilities)) {
    return user.accounting_capabilities.includes(key);
  }
  // Fallback for a stale user object cached before `accounting_capabilities`
  // existed. null/undefined = never configured (master admins default on);
  // an explicit array — including [] — is authoritative.
  const acc = user.accounting_access;
  if (acc === null || acc === undefined) return !!user.is_master_admin;
  return Array.isArray(acc) && acc.includes(key);
};
